import { memo } from 'react';
import type { StaffClaim } from '../../types/staff';
import { EvidenceChip } from './EvidenceChip';
import { EvidenceText } from './EvidenceText';
import { cn } from '../../utils/cn';

type ClaimTone = 'default' | 'watch' | 'poor' | 'plain' | 'good';

interface StaffClaimListProps {
  /** Claim objects with evidence ids, or plain strings carrying inline `[ev:n]` tokens. */
  items: (StaffClaim | string)[];
  tone?: ClaimTone;
  emptyMessage?: string;
  className?: string;
}

const MARKER: Record<ClaimTone, string> = {
  default: 'from-accent-300 to-accent-500 shadow-[0_0_10px_rgb(var(--accent-t-500-rgb)/0.6)]',
  good: 'from-green-300 to-green-500',
  watch: 'from-yellow-300 to-yellow-500',
  poor: 'from-red-300 to-red-500',
  plain: 'from-glass-300 to-glass-400',
};

/**
 * Steward claims as a list. Evidence chips sit on their own line under each claim so
 * sentence text and chips never compete for the same line box.
 */
export const StaffClaimList = memo(({ items, tone = 'default', emptyMessage = 'Nothing recorded yet.', className }: StaffClaimListProps) => {
  if (items.length === 0) {
    return <p className={cn('text-sm text-glass-500 italic', className)}>{emptyMessage}</p>;
  }
  return (
    <ol className={cn('space-y-3', className)}>
      {items.map((item, i) => (
        <li key={i} className="relative pl-5">
          <span
            className={cn('absolute left-0 top-[9px] w-2 h-2 rounded-full bg-gradient-to-br', MARKER[tone])}
            aria-hidden="true"
          />
          {typeof item === 'string' ? (
            <p className="text-sm text-glass-300 leading-7">
              <EvidenceText text={item} />
            </p>
          ) : (
            <>
              <p className="text-sm text-white/90 leading-6">{item.claim}</p>
              {item.evidence_ids.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {item.evidence_ids.map((id) => (
                    <EvidenceChip key={id} id={id} />
                  ))}
                </div>
              )}
            </>
          )}
        </li>
      ))}
    </ol>
  );
});

StaffClaimList.displayName = 'StaffClaimList';
